import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  ConfirmCheckboxQaHistoryCommandService,
  NextSetupSubphaseCommandService,
  PreviousSetupSubphaseCommandService
} from '@app/modules/setup-phase/commands';
import { SetupPhaseService } from '@app/modules/setup-phase/services/setup-phase/setup-phase.service';
import { OrderPhaseFooterCommandBase, OrderPhaseService } from '@app/shared/components';
import { nav, setupPhaseFooterRoutes } from '@app/shared/utils';
import { QaHistoryService } from '@app/modules/qa-history/services/qa-history/qa-history.service';
import { Subscription } from 'rxjs';
import { RunPhaseType, RunSubPhaseType } from 'chronos-core-client';
import { QaHistoryDsService, PatternInfoDsService, ActiveOrderDsService } from '@app/core/data-services';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-setup-qa-history',
  templateUrl: './setup-qa-history.component.html',
  styleUrls: ['./setup-qa-history.component.scss']
})
export class SetupQaHistoryComponent implements OnInit, OnDestroy {
  private previousCommandService: PreviousSetupSubphaseCommandService;
  private nextCommandService: NextSetupSubphaseCommandService;
  private firstCommandService: OrderPhaseFooterCommandBase;
  private subscriptions = new Subscription();
  private isQaHistoryConfirmedAutomatically = false;

  constructor(
    private setupPhaseService: SetupPhaseService,
    private qaHistoryDsService: QaHistoryDsService,
    private setupFooterService: OrderPhaseService,
    private qaHistoryService: QaHistoryService,
    private activeOrderDsService: ActiveOrderDsService,
    private patternInfoDsService: PatternInfoDsService
  ) {}

  public ngOnInit(): void {
    const productionOrderId = this.activeOrderDsService.getActiveOrderId();
    this.previousCommandService = new PreviousSetupSubphaseCommandService(this.setupFooterService);
    this.nextCommandService = new NextSetupSubphaseCommandService(this.setupFooterService);

    this.subscriptions.add(
      this.qaHistoryDsService.isLineClearanceEnableForSetup().subscribe((isLineClearanceEnable) => {
        this.setBackCommand(isLineClearanceEnable);
      })
    );
    this.subscriptions.add(
      this.patternInfoDsService.isPatternAvailableForProductionOrder(productionOrderId).subscribe((isPatternAvailable) => {
        this.setNextCommands(isPatternAvailable);
        this.initFooterCheckboxSubs();
      })
    );

    this.activeOrderDsService
      .isActiveOrderQaHistoryConfirmedAutomatically()
      .subscribe((data) => (this.isQaHistoryConfirmedAutomatically = data));
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private setNextCommands(isPatternAvailable: boolean): void {
    if (isPatternAvailable) {
      this.nextCommandService.setNavigationParams(RunSubPhaseType.SetupPatternInfo, nav.routes.setupPatternInfo);
    } else {
      this.nextCommandService.setNavigationParams(RunSubPhaseType.SetupParameter, nav.routes.setupParameters);
    }
    this.firstCommandService = new ConfirmCheckboxQaHistoryCommandService(this.qaHistoryService);
    this.firstCommandService.isCheckBoxAlreadyConfirmed = this.firstCommandService.enabled = this.isQaHistoryConfirmedAutomatically;
    this.setupPhaseService.setSetupCommands(this.previousCommandService, this.nextCommandService, this.firstCommandService);
  }

  private setBackCommand(isLineClearanceAvailable: boolean): void {
    if (isLineClearanceAvailable) {
      this.previousCommandService.setNavigationParams(nav.routes.setupLineClearance);
      this.previousCommandService.enabled = true;
      this.setBackCommandExecution();
    } else {
      this.previousCommandService.enabled = false;
    }
  }

  private initFooterCheckboxSubs(): void {
    this.subscriptions.add(
      this.qaHistoryService.isActiveOrderQaHistoryChecked().subscribe((isChecked) => {
        this.updateFooterButtons(isChecked || this.isQaHistoryConfirmedAutomatically);
      })
    );
  }

  private updateFooterButtons(isChecked: boolean): void {
    this.firstCommandService.enabled = isChecked;
    this.firstCommandService.required = !isChecked;
    this.nextCommandService.enabled = isChecked;
  }

  private setBackCommandExecution() {
    // Check if the signature is enabled
    this.previousCommandService.execute = () => {
      this.setupFooterService
        .goToNextSubPhase(nav.routes.setupLineClearance, RunPhaseType.Setup, RunSubPhaseType.SetupLineClearance, setupPhaseFooterRoutes)
        .pipe(finalize(() => {}))
        .subscribe();
    };
  }
}
